// Fonctions pures du widget chatbot.
// Rien ici ne touche au DOM pendant le rendu : les fonctions qui lisent
// window/document ne s'appellent que depuis des effets ou des handlers.
import { escapeHtml, locale, t } from '@/lib/i18n'
import type { SystemAction } from './types'

// Point de bascule du panneau plein écran. Doit rester aligné sur la media
// query de chatbot_widget.css et de webview_bottom_nav.css.
export const CHATBOT_FULLSCREEN_QUERY = '(max-width: 767px), (max-width: 1024px) and (max-height: 500px)'

export function isFullscreenPanel(): boolean {
  return !window.matchMedia || window.matchMedia(CHATBOT_FULLSCREEN_QUERY).matches
}

// ─── Textes ─────────────────────────────────────────────────────────────────
// Les clés absentes des dictionnaires retombent sur un texte en dur
// (voir DEVIATIONS.md : clés à ajouter dans les 4 langues).

const isEnglish = () => locale() === 'en'

export const texts = {
  typing: () => t('chatbot.widget.typing_indicator'),
  error: () => t('chatbot.widget.error_generic'),
  networkError: () => t('chatbot.widget.error_network'),
  justNow: () => t('chatbot.widget.just_now'),
  timeout: () => t('chatbot.widget.loading.timeout'),
  loadingMessages: () => [1, 2, 3, 4, 5].map((i) => t(`chatbot.widget.loading.message_${i}`)),
  humorMessages: () => [1, 2, 3, 4, 5].map((i) => t(`chatbot.widget.loading.humor_${i}`)),
  retry: () => t('chatbot.widget.retry'),
  actionStarted: () => t('chatbot.widget.action_started', { default: 'Action started successfully!' }),
  runAction: () => t('chatbot.widget.run_action', { default: 'Run Action' }),
  upgradeNow: () => t('chatbot.widget.subscription_required.upgrade_now'),
  upgradeRequired: () =>
    t('chatbot.widget.upgrade_required', { default: 'Please upgrade your subscription to use this feature.' }),
}

/** Préfixe ⚠️, sans le doubler quand le texte (traduit ou serveur) l'a déjà. */
export function warn(message: string): string {
  return message.startsWith('⚠️') ? message : `⚠️ ${message}`
}

// to_json de Rails (escape_html_entities_in_json) : < > & échappés.
export function railsJson(value: unknown): string {
  return JSON.stringify(value).replace(/[<>&\u2028\u2029]/g, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`)
}

// ─── Dates relatives (figées à l'insertion) ─────────────────────────────────

export function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const diffMs = Date.now() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  const en = isEnglish()

  if (diffMins < 1) return texts.justNow()
  if (diffMins < 60) {
    return t('chatbot.widget.time_ago.minutes', { count: diffMins, default: en ? '%{count} min ago' : 'il y a %{count} min' })
  }
  if (diffHours < 24) {
    return t('chatbot.widget.time_ago.hours', { count: diffHours, default: en ? '%{count}h ago' : 'il y a %{count}h' })
  }
  if (diffDays < 7) {
    return t('chatbot.widget.time_ago.days', { count: diffDays, default: en ? '%{count}d ago' : 'il y a %{count}j' })
  }
  return date.toLocaleDateString(document.documentElement.lang || 'en', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ─── Mise en forme des messages (copie exacte, pas de librairie markdown) ───

export function formatMessageContent(content: string | null | undefined): string {
  if (!content) return ''

  // Échappement d'abord (XSS)
  let formatted = content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

  formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  formatted = formatted.replace(/\*(.+?)\*/g, '<em>$1</em>')
  formatted = formatted.replace(/\n/g, '<br>')

  formatted = formatted.replace(/^\d+\.\s+(.+)$/gm, '<li>$1</li>')
  if (formatted.includes('<li>')) {
    formatted = formatted.replace(/(<li>.*<\/li>)/s, '<ol>$1</ol>')
  }

  formatted = formatted.replace(/^[-*]\s+(.+)$/gm, '<li>$1</li>')
  if (formatted.includes('<li>') && !formatted.includes('<ol>')) {
    formatted = formatted.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
  }

  const paragraphs = formatted.split('<br><br>')
  if (paragraphs.length > 1) {
    formatted = paragraphs
      .map((p) => (p.trim() && !p.includes('<ul>') && !p.includes('<ol>') && !p.includes('<li>') ? `<p>${p}</p>` : p))
      .join('')
  }

  return formatted
}

/** Bouton « réessayer » ajouté en dernier enfant de .message-content. */
export function retryButtonHtml(content: string): string {
  return `<button class="retry-btn" title="${escapeHtml(texts.retry())}" data-retry-content="${escapeHtml(content)}"><i class="fas fa-redo"></i></button>`
}

// ─── Actions système ────────────────────────────────────────────────────────

const LEGACY_ACTION_URLS: Record<string, string> = {
  refresh_portfolio: '/dashboard/refresh',
  refresh_opportunities: '/dashboard/opportunities/refresh',
  create_simulation: '/simulations/new',
}

type LegacyActionSystem = { type: string; params?: Record<string, unknown> }

function fromLegacy(actionSystem: LegacyActionSystem): SystemAction {
  const type = actionSystem.type
  return {
    type,
    title: t(`chatbot.system_actions.${type}.title`, { default: texts.runAction() }),
    subtitle: t(`chatbot.system_actions.${type}.subtitle`, { default: '' }),
    url: LEGACY_ACTION_URLS[type] || '/',
    method: type === 'create_simulation' ? 'GET' : 'POST',
    params: actionSystem.params || {},
  }
}

// Ancien format : l'action en JSON dans le contenu ({"action_system": …}).
export function parseSystemActionFromContent(content: string | null | undefined): SystemAction | null {
  try {
    const parsed = JSON.parse(content || '')
    if (parsed && parsed.action_system) return fromLegacy(parsed.action_system)
  } catch {
    // pas du JSON
  }
  return null
}

// Action d'un message (objet, ou chaîne JSON). Les anciennes lignes portent
// {"action_system": {type, params}} au lieu de l'action complète.
export function normalizeSystemAction(value: unknown): SystemAction | null {
  let action = value
  if (typeof action === 'string') {
    try {
      action = JSON.parse(action)
    } catch (e) {
      console.error('Failed to parse system_action string:', e)
    }
  }
  if (!action || typeof action !== 'object') return null
  const legacy = (action as { action_system?: LegacyActionSystem }).action_system
  if (!(action as SystemAction).type && legacy?.type) return fromLegacy(legacy)
  return (action as SystemAction).type ? (action as SystemAction) : null
}

// ─── Mode app (isWebviewMode / preserveWebviewInUrl) ──────────────────────

export function isWebviewMode(): boolean {
  const params = new URLSearchParams(window.location.search)
  if (params.get('webview') === 'true' || params.get('app') === 'true') return true
  if (document.body.classList.contains('webview-mode')) return true
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } }).Capacitor
  if (cap) {
    if (cap.isNativePlatform && cap.isNativePlatform()) return true
    if (cap.getPlatform && ['ios', 'android'].includes(cap.getPlatform())) return true
  }
  return /capacitor|ionic/i.test(navigator.userAgent || '')
}

export function preserveWebviewInUrl(url: string): string {
  if (!url || !isWebviewMode()) return url
  try {
    const u = new URL(url, window.location.origin)
    if (!u.searchParams.has('webview') && !u.searchParams.has('app')) u.searchParams.set('webview', 'true')
    return u.toString()
  } catch {
    return `${url}${url.includes('?') ? '&' : '?'}webview=true`
  }
}
